import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/services/auth0/session";
import axios from "axios";

const API = import.meta.env.VITE_API_URL;

// #262 — distinct from the legacy customer-credit.ts which targets the
// CustomerBalance ("account balance") primitive. This module wraps the
// CustomerCredit (voucher wallet) endpoints.

export type CustomerCreditKind = "AMOUNT" | "FREE_LINE_ITEM";
export type CustomerCreditScope = "PICKUP" | "DELIVERY";
export type CustomerCreditSourceKind =
  | "TIER_PERK"
  | "SERVICE_RECOVERY"
  | "ADMIN_MANUAL"
  | "SLA_MISS"
  | "VOLUME_REBATE"
  | "PROMOTION_RULE";

export interface CustomerCredit {
  id: string;
  customerId: string;
  carrierId: string | null;
  ruleId: string | null;
  kind: CustomerCreditKind;
  scope: CustomerCreditScope | null;
  amountCents: number | null;
  validFrom: string;
  validUntil: string | null;
  redeemedAt: string | null;
  redeemedOrderId: string | null;
  redemptionCents: number | null;
  reason: string;
  sourceKind: CustomerCreditSourceKind;
  issuedAt: string;
  issuedByUser?: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  redeemedOrder?: { id: string; orderNumber: number | null } | null;
}

export interface CustomerCreditsSummary {
  amountCents: number;
  freeLineItemCount: { PICKUP: number; DELIVERY: number };
}

export interface IssueCreditRequest {
  carrierId: string;
  kind: CustomerCreditKind;
  scope?: CustomerCreditScope;
  amountCents?: number;
  reason: string;
  validUntil?: string;
}

/**
 * Admin view — all credits including expired.
 */
export function useCustomerCreditsAdmin(customerId: string | undefined) {
  const { token } = useSession();
  return useQuery<CustomerCredit[], Error>({
    queryKey: ["admin-customer-credits", customerId],
    enabled: !!token && !!customerId,
    queryFn: async () => {
      const res = await axios.get<{ data: CustomerCredit[] }>(
        `${API}/api/v2/customers/${customerId}/credits?status=all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    },
  });
}

export function useCustomerCreditsSummary(customerId: string | undefined) {
  const { token } = useSession();
  return useQuery<CustomerCreditsSummary, Error>({
    queryKey: ["customer-credits-summary", customerId],
    enabled: !!token && !!customerId,
    queryFn: async () => {
      const res = await axios.get<{ data: CustomerCreditsSummary }>(
        `${API}/api/v2/customers/${customerId}/credits/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    },
  });
}

export function useIssueCustomerCredit(customerId: string | undefined) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation<CustomerCredit, Error, IssueCreditRequest>({
    mutationFn: async (body) => {
      const res = await axios.post<{ data: CustomerCredit }>(
        `${API}/api/v2/admin/customers/${customerId}/credits`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin-customer-credits", customerId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["customer-credits-summary", customerId],
      });
    },
  });
}
